import { useState } from 'react';
import DealCard from './DealCard';
import { isDealInStage, type Deal, type StageItem } from './types';

interface DealsBoardProps {
  deals: Deal[];
  stages: StageItem[];
  onStageChange: (dealId: number, stageName: string) => void;
  onEdit: (deal: Deal) => void;
  onDelete: (dealId: number) => void;
  onGoToChat: (conversationId?: number | null) => void;
}

/**
 * The kanban board. Drag state lives here because nothing outside the board
 * needs it; the page only hears about a completed move.
 */
export default function DealsBoard({
  deals,
  stages,
  onStageChange,
  onEdit,
  onDelete,
  onGoToChat,
}: DealsBoardProps) {
  const [draggedDealId, setDraggedDealId] = useState<number | null>(null);
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);

  const handleDragEnd = () => {
    setDraggedDealId(null);
    setDragOverStageId(null);
  };

  return (
    <div className="kanban-board">
      {stages.map((stage, stageIndex) => {
        const stageDeals = deals.filter((deal) => isDealInStage(deal, stage, stageIndex, stages));
        const stageTotal = stageDeals.reduce((sum, deal) => sum + (deal.amount || 0), 0);
        const StageIcon = stage.icon;

        const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
        };
        const handleDragEnter = () => setDragOverStageId(stage.id);
        const handleDragLeave = () => setDragOverStageId(null);
        const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
          event.preventDefault();
          handleDragEnd();
          const dealId = Number(event.dataTransfer.getData('text/plain'));
          if (dealId) onStageChange(dealId, stage.id);
        };

        return (
          <div
            key={stage.id}
            className={`kanban-column ${dragOverStageId === stage.id ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="column-header" style={{ borderTopColor: stage.color }}>
              <div className="col-title-row">
                <span className="col-icon" style={{ color: stage.color, background: stage.bg }}>
                  <StageIcon size={16} />
                </span>
                <h3 className="col-name">{stage.label}</h3>
                <span className="col-count">{stageDeals.length}</span>
              </div>
              <div className="col-total">{stageTotal.toLocaleString('ru-RU')} ₽</div>
            </div>

            <div className="column-cards">
              {stageDeals.length === 0 ? (
                <div className="empty-column-placeholder">Перетащите сюда заявку</div>
              ) : (
                stageDeals.map((deal) => (
                  <DealCard
                    key={deal.id}
                    deal={deal}
                    stages={stages}
                    isDragging={draggedDealId === deal.id}
                    onDragStart={setDraggedDealId}
                    onDragEnd={handleDragEnd}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onGoToChat={onGoToChat}
                    onStageChange={onStageChange}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
